// Filters, segmented controls, notices and centered states.

import type { CSSProperties } from "react";
import { Icon } from "./Icon.tsx";

export interface Option<T extends string> {
  value: T;
  label: string;
}

/** A capsule filter backed by a native select, so keyboards, screen readers and phones all work. */
export function FilterSelect<T extends string>({ label, value, options, onChange, hideLabel }: {
  label: string;
  value: T;
  options: readonly Option<T>[];
  onChange: (value: T) => void;
  hideLabel?: boolean;
}) {
  const selected = options.find((option) => option.value === value) ?? options[0];
  return (
    <span className="cv-filter cv-select">
      {!hideLabel && <span className="k">{label}</span>}
      <b>{selected?.label}</b>
      <Icon name="chevronDown" size="sm" />
      <select aria-label={label} value={value} onChange={(event) => onChange(event.target.value as T)}>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </span>
  );
}

export function Segmented<T extends string>({ label, value, options, onChange }: {
  label: string;
  value: T;
  options: readonly Option<T>[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="cv-seg" role="group" aria-label={label}>
      {options.map((option) => (
        <button key={option.value} type="button" aria-pressed={option.value === value} onClick={() => onChange(option.value)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Notice({ tone = "warning", icon, children, action, style }: {
  tone?: "warning" | "info" | "danger";
  icon?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  style?: CSSProperties;
}) {
  const className = tone === "warning" ? "cv-notice" : `cv-notice is-${tone}`;
  return (
    <div className={className} style={{ alignItems: action ? "center" : undefined, ...style }} role={tone === "danger" ? "alert" : undefined}>
      <Icon name={icon ?? (tone === "warning" ? "road" : tone === "danger" ? "warning" : "info")} size={action ? "lg" : undefined} />
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
      {action}
    </div>
  );
}

export function StateCard({ icon, iconTint, title, children, id }: {
  icon: string;
  iconTint?: string;
  title: string;
  children: React.ReactNode;
  id?: string;
}) {
  const headingId = id ?? `state-${title.replace(/\W+/g, "-").toLowerCase()}`;
  return (
    <div className="cv-center" style={{ paddingTop: 8 }}>
      <section className="cv-card cv-state" aria-labelledby={headingId}>
        <div className="cv-state-icon" style={iconTint ? { background: iconTint } : undefined}><Icon name={icon} /></div>
        <h1 id={headingId}>{title}</h1>
        {children}
      </section>
    </div>
  );
}

export function Steps({ label, steps }: { label: string; steps: React.ReactNode[] }) {
  return (
    <ol className="cv-steps" aria-label={label}>
      {steps.map((step, index) => (
        <li key={index}><span className="cv-step-n" aria-hidden="true">{index + 1}</span><span>{step}</span></li>
      ))}
    </ol>
  );
}
